import React, {useState} from 'react';

import ThreeDotsDropdownMenu from 'app/general/widgets/ThreeDotsDropdownMenu';
import {
  useAddArticle,
  useArticles,
  useCategories,
  useDeleteCategory,
} from 'app/kbs/api';
import {gt} from 'app/utils/i18n';

import {KBTreeCreateCategoryItem} from './KBTreeCreateCategoryItem';
import {KBTreeEditCategory} from './KBTreeEditCategory';

import {KBTreeArticleItem, KBTreeCreateArticleItem} from '../KBTreeArticleItem';
import {useDeleteModal} from '../KBTreeDeleteItemModal';
import {FALLBACK_ICON} from '../KBTreeEditItemStructure';
import {useExpandContext} from '../KBTreeExpandContext';
import {KBTreeCollapsibleItemStructure} from '../KBTreeStructures';

import type {MenuItemsType} from 'app/general/widgets/ThreeDotsDropdownMenu';
import type {
  KBCategorySerializedType,
  KnowledgebaseSerializedType,
  SearchSerializedType,
} from 'sweet-types';

type Props = {
  kb: KnowledgebaseSerializedType;
  category: KBCategorySerializedType;
  afterAction?: () => Promise<unknown>;
};
export const KBTreeCategoryItem = ({kb, category, afterAction}: Props) => {
  const {expanded, setExpanded} = useExpandContext(category.url);
  const [editing, setEditing] = useState<boolean>(false);
  const setDeleting = useDeleteModal();

  const [addArticle] = useAddArticle();
  const [deleteCategory] = useDeleteCategory();

  const {data: articles} = useArticles({
    kb_id: kb.id,
    category_id: category.id,
  });

  const {
    data: categories,
    isLoading: isCategoriesLoading,
    refetch: refreshChildCategories,
  } = useCategories({
    kb_id: kb.id,
    parent_category_id: category.id,
  });

  function createArticle(value: SearchSerializedType<'policy' | 'procedure'>) {
    const payload = {
      document_content_type: value.content_type,
      document_object_id: value.object_id,
      knowledgebase_url: kb.url,
      category_url: category.url,
    };
    return addArticle(payload).unwrap();
  }

  const doDeleteCategory = (payload: KBCategorySerializedType) => {
    // A promise is needed to close the modal; so I had to wrap
    // RTK mutation with a function to avoid unwrapping in the child component.
    return deleteCategory(payload).unwrap();
  };

  function actionsGetter(): MenuItemsType {
    return [
      {
        text: gt('View in Knowledge Base'),
        key: 'view-category',
        icon: 'far fa-eye',
        condition: () => true,
        callback: () =>
          new Promise((resolve) => {
            const win: Window = window;
            resolve((win.location = category.html_url));
          }),
      },
      {
        text: gt('Change name or icon'),
        key: 'edit-category',
        icon: 'fas fa-pen',
        condition: () => true,
        callback: () => setEditing(true),
      },
      {
        text: gt('Delete'),
        icon: 'far fa-trash',
        key: 'remove-category',
        condition: () => true,
        callback: () =>
          setDeleting({
            object: category,
            action: doDeleteCategory,
            callback: afterAction,
            help: gt(
              'All sub-categories and articles will be removed from this knowledge base.'
            ),
          }),
      },
    ];
  }

  return (
    <KBTreeCollapsibleItemStructure
      icon={editing ? null : category.icon || FALLBACK_ICON}
      disableToggleButton={editing}
      title={
        editing ? (
          <KBTreeEditCategory
            kb={kb}
            parentCategoryURL={
              category.parent_category ? category.parent_category : ''
            }
            category={category}
            onCancel={() => setEditing(false)}
            afterAction={() => {
              setEditing(false);
              afterAction && afterAction();
            }}
          />
        ) : (
          category.name
        )
      }
      onToggleExpand={() => setExpanded(!expanded)}
      expanded={expanded}
      loading={expanded && isCategoriesLoading}
      actionsChildren={
        <ThreeDotsDropdownMenu
          object={category}
          actionsGetter={actionsGetter}
        />
      }
    >
      {expanded && categories && categories?.count > 0
        ? categories?.results.map((c, index) => (
            <KBTreeCategoryItem
              key={index}
              kb={kb}
              category={c}
              afterAction={async () => {
                refreshChildCategories();
              }}
            />
          ))
        : null}
      {expanded && articles && articles?.count > 0
        ? articles?.results.map((a, index) => (
            <KBTreeArticleItem key={index} kb={kb} article={a} />
          ))
        : null}
      <KBTreeCreateArticleItem
        create={createArticle}
        contentTypes={['procedure', 'policy']}
        placeholderText={gt('Select a procedure or policy to add...')}
        buttonDescription={gt('Add new Article')}
        excludeParameter={{exclude_category_id: category.id}}
      />
      <KBTreeCreateCategoryItem kb={kb} parentCategoryURL={category.url} />
    </KBTreeCollapsibleItemStructure>
  );
};
